import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getDisclaimerPack, type Jurisdiction } from "./jurisdiction";
import { getProtocol } from "./protocols/definitions";

const FREE_MONTHLY_LIMIT = 1;

const InputSchema = z.object({
  imageBase64: z.string().min(100).max(22_000_000),
  goal: z.enum([
    "skin", "weight", "muscle", "antiaging",
    "post_aesthetic", "pre_post_op", "meno_andro", "onco_recovery",
  ]),
  environment: z.enum(["sandbox", "live"]),
  storePhotos: z.boolean().optional().default(false),
  locale: z.enum(["fr", "en", "nl"]).default("fr"),
  jurisdiction: z
    .enum(["EU_STRICT", "UK", "CH_NO", "US", "CA", "AU_NZ", "DEFAULT"])
    .default("DEFAULT"),
  userNotes: z.string().trim().max(2000).optional(),
  userActions: z
    .array(
      z.object({
        kind: z.string().max(40),
        label: z.string().max(120),
      }),
    )
    .max(20)
    .optional(),
});

const goalLabels: Record<"fr" | "en" | "nl", Record<string, string>> = {
  fr: { skin: "Peau & Éclat", weight: "Perte de poids & Vitalité", muscle: "Tonus musculaire", antiaging: "Anti-âge & Longévité", post_aesthetic: "Post-traitement esthétique", pre_post_op: "Pré/Post-opératoire", meno_andro: "Ménopause / Andropause", onco_recovery: "Récupération oncologique" },
  en: { skin: "Skin & Glow", weight: "Fitness & Vitality", muscle: "Muscle Tone", antiaging: "Anti-ageing & Longevity", post_aesthetic: "Post-aesthetic recovery", pre_post_op: "Pre/Post-op tracking", meno_andro: "Menopause / Andropause", onco_recovery: "Oncology recovery" },
  nl: { skin: "Huid & glans", weight: "Fitheid & vitaliteit", muscle: "Spiertonus", antiaging: "Anti-veroudering & levensduur", post_aesthetic: "Post-esthetisch herstel", pre_post_op: "Pre/Post-operatief", meno_andro: "Menopauze / Andropauze", onco_recovery: "Oncologisch herstel" },
};

const localeName: Record<"fr" | "en" | "nl", string> = {
  fr: "français",
  en: "English",
  nl: "Nederlands",
};

const serverErrors: Record<"fr" | "en" | "nl", Record<string, string>> = {
  fr: {
    limit: `Limite gratuite atteinte (${FREE_MONTHLY_LIMIT} analyse / mois). Passez à Premium pour continuer.`,
    rate: "Trop de requêtes, réessayez dans un instant.",
    credits: "Crédits IA épuisés. Ajoutez du crédit dans Lovable Cloud.",
    partial: "Analyse partielle",
    retry: "Réessayez",
  },
  en: {
    limit: `Free limit reached (${FREE_MONTHLY_LIMIT} analysis / month). Go Premium to continue.`,
    rate: "Too many requests, please try again shortly.",
    credits: "AI credits exhausted. Add credit in Lovable Cloud.",
    partial: "Partial analysis",
    retry: "Try again",
  },
  nl: {
    limit: `Gratis limiet bereikt (${FREE_MONTHLY_LIMIT} analyse / maand). Upgrade naar Premium om door te gaan.`,
    rate: "Te veel aanvragen, probeer het zo meteen opnieuw.",
    credits: "AI-credits opgebruikt. Voeg krediet toe in Lovable Cloud.",
    partial: "Gedeeltelijke analyse",
    retry: "Probeer opnieuw",
  },
};

let _admin: any = null;
function getAdmin() {
  if (!_admin) {
    _admin = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_SERVICE_ROLE_KEY!);
  }
  return _admin;
}

export const analyzeSelfie = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => InputSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const admin = getAdmin();

    // Check subscription against the env the client is currently using
    const { data: isPremium } = await admin.rpc("has_active_subscription", {
      user_uuid: userId,
      check_env: data.environment,
    });

    // Free access can also come from an active admin-granted code
    const { data: grantRows } = await admin
      .from("access_grants")
      .select("tier, active, revoked_at")
      .eq("beneficiary_user_id", userId)
      .eq("active", true);
    const hasGrant = (grantRows ?? []).some((g: any) => !g.revoked_at);

    if (!isPremium && !hasGrant) {
      const { data: count } = await admin.rpc("count_analyses_this_month", {
        user_uuid: userId,
      });
      if ((count ?? 0) >= FREE_MONTHLY_LIMIT) {
        throw new Error(serverErrors[data.locale].limit);
      }
    }

    // Fetch the most recent prior analysis for the same goal to ground the model
    // and prevent dichotomous swings on photos taken close together.
    const { data: lastRows } = await admin
      .from("analyses")
      .select("created_at, summary, score, metrics")
      .eq("user_id", userId)
      .eq("goal", data.goal)
      .order("created_at", { ascending: false })
      .limit(1);
    const last = lastRows?.[0] ?? null;
    const hoursSinceLast = last
      ? Math.max(0, (Date.now() - new Date(last.created_at).getTime()) / 36e5)
      : null;

    let temporalGuidance = "";
    if (hoursSinceLast !== null) {
      const prev = {
        hoursAgo: Number(hoursSinceLast.toFixed(1)),
        summary: last!.summary,
        metrics: last!.metrics,
      };
      if (hoursSinceLast < 3) {
        temporalGuidance = `\n\nCONTEXTE TEMPOREL — ATTENTION :
L'utilisateur a déjà été analysé il y a ${prev.hoursAgo} h (<3 h). La physiologie ne peut PAS avoir changé significativement sur cet intervalle. Toute variation de score >5 points par rapport à l'analyse précédente DOIT être justifiée explicitement dans "summary" par un facteur exogène plausible (éclairage, balance des blancs, filtre, angle, hydratation, sommeil très récent, coup de soleil, maquillage, sueur post-effort). Si aucun facteur exogène n'est visible, garde les scores à ±3 points de l'analyse précédente et indique-le. N'émets JAMAIS de recommandations dichotomiques (ex: "votre peau s'est dégradée" puis "votre peau est radieuse") sans cette justification.
Analyse précédente: ${JSON.stringify(prev)}`;
      } else if (hoursSinceLast < 24) {
        temporalGuidance = `\n\nCONTEXTE TEMPOREL :
Dernière analyse il y a ${prev.hoursAgo} h. Les variations physiologiques restent limitées sur <24 h. Reste prudent : si un score diffère de >10 points, justifie-le par un facteur exogène ou conditionnel (sommeil, hydratation, éclairage, filtre).
Analyse précédente: ${JSON.stringify(prev)}`;
      } else {
        temporalGuidance = `\n\nCONTEXTE :
Dernière analyse il y a ${prev.hoursAgo} h. Tu peux mesurer une progression réelle, mais reste calibré par rapport à: ${JSON.stringify(prev)}.`;
      }
    }

    // User-declared context between two captures (haircut, sun, new cream, makeup, filter, …)
    let userContextGuidance = "";
    const actionsLabels = (data.userActions ?? []).map((a) => `${a.kind}: ${a.label}`);
    if ((data.userNotes && data.userNotes.length > 0) || actionsLabels.length > 0) {
      userContextGuidance = `\n\nCONTEXTE UTILISATEUR (déclaré entre les deux prises) :
- Actions/évènements : ${actionsLabels.length ? actionsLabels.join(" | ") : "(aucun)"}
- Notes libres : ${data.userNotes ?? "(aucune)"}
Intègre ce contexte pour pondérer les variations apparentes (ex: coupe récente → pas de jugement sur la densité capillaire; coup de soleil → expliquer rougeurs; nouvelle crème → relier aux changements de texture). Mentionne explicitement dans "summary" si une action déclarée explique une variation.`;
    }

    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("Missing LOVABLE_API_KEY");

    const pack = getDisclaimerPack(data.jurisdiction as Jurisdiction, data.locale);
    const protocolDef = getProtocol(data.goal);
    const protocolGuidance = protocolDef
      ? `\n\nCONTEXTE PROTOCOLE SPÉCIALISÉ :\n${protocolDef.aiContext[data.locale]}`
      : "";
    const jurisdictionGuidance = `\n\nCADRE RÉGLEMENTAIRE LOCAL (${data.jurisdiction}) — IMPÉRATIF :\n${pack.aiGuidance}\nPour TOUT tip de catégorie "supplement", inclus dans 'disclaimer' (en plus du rappel pro de santé) la formulation conforme à cette juridiction.`;

    const systemPrompt = `Tu es un coach photo IA bienveillant et précis, spécialiste en analyse faciale wellness. Tu analyses un selfie en lien avec l'objectif: ${goalLabels[data.locale][data.goal]}.
Réponds intégralement en ${localeName[data.locale]} : summary, labels des metrics, titres, détails, fréquence et disclaimer. Ne mélange jamais français, anglais et néerlandais dans une même réponse.
Réponds UNIQUEMENT en JSON valide, sans markdown, au format:
{
  "summary": "1 phrase synthétique sur l'état actuel (<=140 caractères)",
  "metrics": [
    {"label": "Luminosité Peau", "score": 0-100, "delta": "+X%" ou "Stable"},
    {"label": "Repos Oculaire", "score": 0-100, "delta": "..."},
    {"label": "Posture / Symétrie", "score": 0-100, "delta": "..."}
  ],
  "tips": [
    {
      "title": "Titre court et concret (max 50 car.)",
      "detail": "2-4 phrases : QUOI faire, COMMENT (protocole précis), COMBIEN (fréquence/durée/dose), POURQUOI (mécanisme bref). Privilégie les habitudes durables plutôt que les astuces ponctuelles.",
      "category": "habit | nutrition | supplement | posture | sleep | skincare | hydration | exercise | mindset",
      "horizon": "daily | weekly | 4-weeks | 12-weeks",
      "frequency": "ex: 'tous les soirs', '3×/sem', '1×/jour'",
      "disclaimer": "OBLIGATOIRE si category = supplement ou nutrition spécifique. Rappelle de consulter un médecin/pharmacien, mentionne les interactions/contre-indications éventuelles."
    }
  ]
}
RÈGLES DE QUALITÉ DES CONSEILS (impératives) :
- Fournis 5 tips (et non 3) couvrant des leviers VARIÉS : au minimum 1 habitude quotidienne, 1 conseil nutrition, 1 conseil posture/exercice ou sommeil, 1 conseil skincare/hydratation, et 1 conseil orienté permanence (horizon 4-weeks ou 12-weeks).
- Privilégie la PERMANENCE : habitudes ancrables, pas de "hacks" éphémères. Chaque tip doit pouvoir tenir ≥4 semaines.
- Sois SPÉCIFIQUE et ACTIONNABLE : doses, durées, fréquences, exemples d'aliments, angles de posture, températures d'eau, types de filtres SPF, etc.
- Pour les suppléments : ne recommande que des composés grand public bien documentés (ex: vitamine D3, oméga-3, créatine monohydrate, magnésium bisglycinate, collagène hydrolysé, zinc) avec une fourchette de dose usuelle. Mentionne TOUJOURS dans "disclaimer" : "Avis non médical. Consultez un professionnel de santé avant toute supplémentation, surtout en cas de grossesse, traitement en cours ou pathologie."
- Pour la nutrition : préfère des leviers globaux (apport en protéines, polyphénols, oméga-3, fibres, hydratation) plutôt que des régimes restrictifs.
- Pour la posture : décris un repère concret (oreille alignée avec l'épaule, écran à hauteur des yeux, pause active toutes les 45 min, exercice de rétraction cervicale 10×).
- Pour le sommeil : cible chronotype, régularité, lumière du soir, température de chambre, écrans.
- Ne donne JAMAIS de diagnostic médical. Reste bienveillant et non culpabilisant.

RÈGLE DE COHÉRENCE TEMPORELLE (impérative) :
- La physiologie de la peau, des yeux et du tonus évolue lentement (jours/semaines), pas en heures.
- Si une analyse précédente t'est fournie ci-dessous, calibre tes scores par rapport à elle.
- N'émets jamais de recommandations contradictoires d'une session à l'autre sans expliquer pourquoi (éclairage, filtre, angle, balance des blancs, maquillage, coup de soleil, fatigue ponctuelle, post-effort, etc.).
- En cas de doute sur un changement apparent, attribue-le d'abord aux conditions de prise de vue et mentionne-le dans "summary" ou dans le 1er "tip".${temporalGuidance}${userContextGuidance}${protocolGuidance}${jurisdictionGuidance}`;

    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        messages: [
          { role: "system", content: systemPrompt },
          {
            role: "user",
            content: [
              { type: "text", text: `Analyse ce selfie pour l'objectif: ${goalLabels[data.locale][data.goal]}. Réponds en JSON, intégralement en ${localeName[data.locale]}.` },
              { type: "image_url", image_url: { url: data.imageBase64 } },
            ],
          },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (res.status === 429) throw new Error(serverErrors[data.locale].rate);
    if (res.status === 402) throw new Error(serverErrors[data.locale].credits);
    if (!res.ok) throw new Error(`Erreur IA (${res.status})`);

    const json = await res.json();
    const content = json.choices?.[0]?.message?.content ?? "{}";
    let parsed: any;
    try {
      parsed = JSON.parse(content);
    } catch {
      parsed = { summary: serverErrors[data.locale].partial, metrics: [], tips: [{ title: serverErrors[data.locale].retry, detail: content.slice(0, 200) }] };
    }

    // Optional: store the selfie when the user has explicitly consented.
    let imagePath: string | null = null;
    if (data.storePhotos) {
      try {
        const m = /^data:(image\/[a-zA-Z+-]+);base64,(.+)$/.exec(data.imageBase64);
        if (m) {
          const mime = m[1];
          const ext = mime.split("/")[1].replace("jpeg", "jpg").split("+")[0];
          const bin = atob(m[2]);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const path = `${userId}/${Date.now()}-${crypto.randomUUID()}.${ext}`;
          const { error: upErr } = await admin.storage
            .from("selfies")
            .upload(path, bytes, { contentType: mime, upsert: false });
          if (!upErr) imagePath = path;
          else console.error("Selfie upload failed", upErr);
        }
      } catch (e) {
        console.error("Selfie upload error", e);
      }
    }

    // Log analysis for quota tracking
    await admin.from("analyses").insert({
      user_id: userId,
      goal: data.goal,
      summary: parsed.summary ?? null,
      score: parsed.metrics?.[0]?.score ?? null,
      metrics: parsed.metrics ?? null,
      tips: parsed.tips ?? null,
      image_path: imagePath,
      user_notes: data.userNotes && data.userNotes.length > 0 ? data.userNotes : null,
      user_actions: data.userActions && data.userActions.length > 0 ? data.userActions : null,
    });

    return parsed;
  });
